/**
 * A dónde lleva una notificación cuando se le da clic.
 *
 * POR QUÉ EXISTE. Un drop (19-oct-2026): «si un pedido ya llegó a la agencia de destino, al darle
 * clic a la notificación debería llevarme a la página del pedido en Entregas; ahora no hace nada».
 * El clic sí navegaba: a la `url` guardada en la fila, que era `/m/orders/<id>` — una interfaz móvil
 * retirada cuyo comodín manda al Inicio. Quien ya estaba en el Inicio no veía ningún cambio.
 *
 * ⭐ EL DESTINO SALE DEL HECHO, NO DE LA URL GUARDADA. La fila dice qué pasó (`type`) y con qué
 *    (`data.orderId`, `data.conversationId`…). Con eso se arma la ruta de HOY. La `url` es una foto
 *    de cómo eran las rutas el día que se escribió: las filas viejas siguen en la campana meses
 *    después de que la ruta se mude, y ninguna migración las alcanza en el navegador de cada uno.
 *
 * 🪤 LA URL GUARDADA SE TRADUCE, NO SE DESCARTA. Hay tipos sin entidad (un aviso de integración, el
 *    de una novedad) cuya url sí es el destino. Lo que se traduce son las rutas retiradas, con la
 *    misma tabla que usa la plantilla del servidor (`lasRutasRetiradas`, un espejo); lo que ya es una
 *    ruta del panel pasa igual.
 *
 * Devuelve una ruta del panel SIN el prefijo de la tienda (la navegación lo agrega), una dirección
 * externa (`https://…`, se abre aparte) o `null` si el aviso no lleva a ningún lado.
 */
import { ROUTES } from '@/config/routes.config';
import { laRutaDeHoy } from '@/lib/avisos/lasRutasRetiradas';

export interface AvisoConDestino {
  type: string;
  url: string | null;
  data: Record<string, unknown> | null;
}

const texto = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
const conId = (ruta: string, id: string) => ruta.replace(/:[a-zA-Z]+/, encodeURIComponent(id));

/**
 * Los avisos que hablan de un PEDIDO, y a qué página del pedido llevan. Lo del envío va a Entrega
 * —ahí está la agencia, la guía y el rastreo—; lo de la venta, a la venta.
 */
const PAGINA_DEL_PEDIDO: Record<string, string> = {
  'shipment.at_agency': ROUTES.OPS_ORDER_ENTREGA,
  'envio.destino_no_coincide': ROUTES.OPS_ORDER_ENTREGA,
  // Lo emite el barrido de «no retira su pedido»: el paquete está en la agencia y el cliente no contesta.
  'conversation.sin_respuesta': ROUTES.OPS_ORDER_ENTREGA,
  // La guía de la venta se anuló (la tienda, el almacén o el equipo de Smarty): se vuelve a registrar en Entrega.
  'drop.guia_anulada': ROUTES.OPS_ORDER_ENTREGA,
  // La guía vencida se registró de nuevo (o no se pudo): el tracking nuevo y el botón de registrar están en Entrega.
  'drop.guia_reemitida': ROUTES.OPS_ORDER_ENTREGA,
  'drop.guia_vencida_sin_reemitir': ROUTES.OPS_ORDER_ENTREGA,
  // El courier entrega otro día (su corte) o rechazó el pedido: el día y «Registrar en <courier>» están en Entrega.
  'drop.entrega_al_dia_siguiente': ROUTES.OPS_ORDER_ENTREGA,
  'drop.envio_rechazado_por_el_courier': ROUTES.OPS_ORDER_ENTREGA,
  // 20261029010000: la zona roja del courier (queda en revisión) se ve en la Entrega de la venta.
  'drop.envio_en_revision_del_courier': ROUTES.OPS_ORDER_ENTREGA,
  // El cambio de destino se pide y se responde sobre la guía de la venta.
  'drop.cambio_destino_atendido': ROUTES.OPS_ORDER_ENTREGA,
  'order.created': ROUTES.OPS_ORDER_DETAIL,
  'marketplace.order.created': ROUTES.OPS_ORDER_DETAIL,
};

/** Los avisos de plata del Drop: quien declara espera en Cobros; a quien le aprueban, en su Billetera. */
const PAGINA_FIJA: Record<string, string> = {
  'drop.recarga_declarada': ROUTES.DROPSHIPPING_SMARTYDROP_COBROS,
  'drop.pago_declarado': ROUTES.DROPSHIPPING_SMARTYDROP_COBROS,
  // La liquidación de un courier con API (MERCourier): se concilia sola y se confirma en sus liquidaciones (20261028130000).
  'drop.liquidacion_del_courier': ROUTES.DROPSHIPPING_SMARTYDROP_LIQUIDACIONES_DE_COURIERS,
  // El cuadre de la caja de un courier con el libro: se mira en sus liquidaciones, día por día.
  'drop.cuadre_con_el_courier': ROUTES.DROPSHIPPING_SMARTYDROP_LIQUIDACIONES_DE_COURIERS,
  // La liquidación del courier avisa (20261027140000): Smarty revisa en sus liquidaciones; el courier, en las suyas.
  'drop.rendicion_declarada': ROUTES.DROPSHIPPING_SMARTYDROP_LIQUIDACIONES_DE_COURIERS,
  'drop.couriers_sin_depositar': ROUTES.DROPSHIPPING_SMARTYDROP_LIQUIDACIONES_DE_COURIERS,
  'drop.rendicion_confirmada': ROUTES.ENTREGAS_LIQUIDACIONES,
  'drop.rendicion_rechazada': ROUTES.ENTREGAS_LIQUIDACIONES,
  'drop.dias_sin_deposito': ROUTES.ENTREGAS_LIQUIDACIONES,
  'drop.recarga_aprobada': ROUTES.DROPSHIPPING_SMARTYDROP_SALDO,
  'drop.pago_aprobado': ROUTES.DROPSHIPPING_SMARTYDROP_SALDO,
  'drop.costo_cambio': ROUTES.DROPSHIPPING_SMARTYDROP,
  // La torre de control que no cumple su objetivo de servicio: se mira la torre.
  'torre.servicio_lento': ROUTES.TORRE,
  // Un movimiento de plata que espera firma: se confirma o se rechaza en la bandeja.
  'torre.firma_pendiente': ROUTES.TORRE_POR_RESOLVER,
};

export function aDondeLlevaElAviso(aviso: AvisoConDestino): string | null {
  const datos = aviso.data ?? {};

  const pedido = texto(datos.orderId);
  const paginaDelPedido = PAGINA_DEL_PEDIDO[aviso.type];
  if (paginaDelPedido && pedido) return conId(paginaDelPedido, pedido);

  const conversacion = texto(datos.conversationId);
  if ((aviso.type === 'conversation.handoff_requested' || aviso.type === 'conversation.complaint') && conversacion) {
    return conId(ROUTES.WHATSAPP_CONVERSATION, conversacion);
  }

  const lead = texto(datos.leadId);
  if (aviso.type === 'lead.created' && lead) return conId(ROUTES.APPS_CRM_LEAD_DETAIL, lead);

  if (PAGINA_FIJA[aviso.type]) return PAGINA_FIJA[aviso.type];

  const url = texto(aviso.url);
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return laRutaDeHoy(url.startsWith('/') ? url : `/${url}`);
}
